import React, { useEffect, useRef, useState } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRotateRight } from '@fortawesome/free-solid-svg-icons';
import { CAR_FINANCE_BACKEND_URL } from '../config/env';
import { getUrlParam, trimToUndefined } from '../utils/submission';
import './SignatureLander.css';

/**
 * Signature Lander.
 * URL: ?signature=true&clientid=...&f=...
 * Sign → submit → POST /api/1.0/signature (writes clientSignature to Phoenix).
 */
export default function SignatureLander({ onSubmitted }) {
  const sigCanvas = useRef(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [signaturePadWidth, setSignaturePadWidth] = useState();

  const clientid = trimToUndefined(getUrlParam('clientid'))?.replace(/^=+/, '');
  const firstname =
    trimToUndefined(getUrlParam('firstname')) ||
    trimToUndefined(getUrlParam('f')) ||
    trimToUndefined(getUrlParam('firstName')) ||
    trimToUndefined(getUrlParam('forename'));

  useEffect(() => {
    const updateSignaturePadWidth = () => {
      setSignaturePadWidth(sigCanvas.current?._canvas?.parentElement?.clientWidth);
    };
    updateSignaturePadWidth();

    const sigPad = sigCanvas.current?._sigPad;
    const canvas = sigCanvas.current?._canvas;
    if (sigPad && canvas) {
      const originalCreatePoint = sigPad._createPoint.bind(sigPad);
      sigPad._createPoint = (x, y, pressure) => {
        const rect = canvas.getBoundingClientRect();
        const clampedX = Math.min(Math.max(x, rect.left), rect.right);
        const clampedY = Math.min(Math.max(y, rect.top), rect.bottom);
        return originalCreatePoint(clampedX, clampedY, pressure);
      };
    }

    window.addEventListener('resize', updateSignaturePadWidth);
    return () => window.removeEventListener('resize', updateSignaturePadWidth);
  }, []);

  const signatureAsSvg = () => {
    const sourcePad = sigCanvas.current?._sigPad;
    if (!sourcePad) return '';

    const strokes = sourcePad.toData();
    const points = strokes.flat();
    if (!points.length) return '';

    const xValues = points.map(({ x }) => x);
    const yValues = points.map(({ y }) => y);
    const minX = Math.min(...xValues);
    const maxX = Math.max(...xValues);
    const minY = Math.min(...yValues);
    const maxY = Math.max(...yValues);
    const strokeWidth = Math.max(maxX - minX, 1);
    const strokeHeight = Math.max(maxY - minY, 1);
    const padding = Math.max(Math.max(strokeWidth, strokeHeight) * 0.08, 8);

    const source = sourcePad.toDataURL('image/svg+xml');
    const sourceMarkup = window.atob(source.split(',')[1]);
    const innerMarkup = sourceMarkup
      .replace(/^.*?<svg[^>]*>/s, '')
      .replace(/<\/svg>\s*$/s, '');

    const viewBox = [
      minX - padding,
      minY - padding,
      strokeWidth + padding * 2,
      strokeHeight + padding * 2,
    ].join(' ');
    const normalizedMarkup =
      `<svg xmlns="http://www.w3.org/2000/svg" ` +
      `viewBox="${viewBox}" width="300" height="150" ` +
      `preserveAspectRatio="xMidYMid meet">${innerMarkup}</svg>`;

    return `data:image/svg+xml;base64,${window.btoa(normalizedMarkup)}`;
  };

  const clear = () => {
    sigCanvas.current?.clear();
    setError('');
  };

  const submit = async () => {
    if (!clientid) {
      setError("We couldn't identify your claim. Please use the link we sent you.");
      return;
    }
    if (!sigCanvas.current || sigCanvas.current.isEmpty()) {
      setError('Please sign in the box below before submitting.');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      const signature = signatureAsSvg();
      const base64 = signature.split(',')[1];
      const res = await fetch(`${CAR_FINANCE_BACKEND_URL}/api/1.0/signature`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': process.env.REACT_APP_DATASTORE_API_KEY,
        },
        body: JSON.stringify({ clientid, signature: base64 }),
      });

      if (!res.ok) throw new Error(`Signature request failed: ${res.status}`);

      const result = await res.json();
      if (onSubmitted) {
        onSubmitted(result);
      } else {
        setSubmitted(true);
      }
    } catch (err) {
      setError("We couldn't save your signature. Please try again.");
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="fdc-form-wrapp">
        <div className="fdc-stepper-wrapp">
          <div className="fdc-shadow-block">
            <div className="fdc-form text-center signature-lander-success">
              <h2 className="title mb-4">Thank you!</h2>
              <p>Your signature has been received.</p>
              <p>We'll be in touch with your next steps.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fdc-form-wrapp">
      <div className="fdc-stepper-wrapp">
        <div className={`fdc-shadow-block ${submitting ? 'active-spinner' : ''}`}>
          <div className="loading-overlay"></div>
          <div className="loader"></div>

          <div className="fdc-form signature-lander">
            <div className="text-center">
              <h2 className="title mb-4">
                Good news{firstname ? ` ${firstname}` : ''}!
              </h2>

              <div className="signature-lander-intro">
                <p className="mb-3">
                  Thank you for choosing us to handle your car finance claim
                </p>
                <p className="mb-4">
                  We are progressing your claim but now need your signature to move forward.
                </p>
              </div>
            </div>

            <section aria-labelledby="signature-instruction">
              <p id="signature-instruction" className="signature-lander-instruction">
                Please sign in the box below
              </p>
              <div className="signature-lander-canvas-wrapper">
                <SignatureCanvas
                  clearOnResize={false}
                  penColor="black"
                  ref={sigCanvas}
                  canvasProps={{
                    className: 'signature-lander-canvas',
                    height: 220,
                    width: signaturePadWidth,
                    'aria-label': 'Signature drawing area',
                  }}
                />
                <button
                  type="button"
                  className="signature-lander-clear"
                  onClick={clear}
                  aria-label="Clear signature"
                  title="Clear signature"
                >
                  <FontAwesomeIcon icon={faRotateRight} />
                </button>
              </div>
            </section>

            <p className="signature-lander-trust text-center mb-0">
              Thank you for your continued trust. We're committed to achieving the best possible
              outcome for you.
            </p>

            {error && <p className="error-message-text text-center mt-3 mb-0">{error}</p>}

            <div className="row gx-2 steps action-button mt-4">
              <div className="col-12 text-center">
                <button
                  type="button"
                  onClick={submit}
                  className="btn-w color3 next-button"
                  disabled={submitting}
                >
                  {submitting ? 'Submitting…' : 'Submit'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
